import React, { useState, useRef, useEffect } from 'react';
import { Popover, CircularProgress, Typography } from '@mui/material';
import { fetchUserById, UserDetails } from '../services/usersService';
import AvatarImage from './AvatarImage';

export interface UserMentionPopoverProps {
    userId: string;
    children: React.ReactNode;
}

export default function UserMentionPopover({ userId, children }: UserMentionPopoverProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }

        const currentTarget = event.currentTarget;

        hoverTimeoutRef.current = setTimeout(() => {
            setAnchorEl(currentTarget);
            if (!userDetails && !loading && !error) {
                fetchUserData();
            }
        }, 500);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        leaveTimeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 300);
    };

    const handlePopoverMouseEnter = () => {
        if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }
    };

    const handlePopoverMouseLeave = () => {
        setAnchorEl(null);
    };

    const fetchUserData = async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await fetchUserById(userId);
            setUserDetails(data);
        } catch (err) {
            console.error('Failed to fetch user details:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
            if (leaveTimeoutRef.current) {
                clearTimeout(leaveTimeoutRef.current);
            }
        };
    }, []);

    const open = Boolean(anchorEl);

    return (
        <>
            <span
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    color: '#0078d4',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'text-decoration 0.2s',
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                }}
            >
                {children}
            </span>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableRestoreFocus
                sx={{
                    zIndex: 9999,
                    pointerEvents: 'none',
                    '& .MuiPopover-paper': {
                        pointerEvents: 'auto',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                        borderRadius: '4px',
                        marginTop: '8px',
                    },
                }}
                slotProps={{
                    paper: {
                        onMouseEnter: handlePopoverMouseEnter,
                        onMouseLeave: handlePopoverMouseLeave,
                    },
                }}
            >
                <div className="p-4 min-w-[280px] max-w-[320px]">
                    {loading ? (
                        <div className="flex justify-center items-center py-4">
                            <CircularProgress size={24} />
                        </div>
                    ) : error ? (
                        <div className="text-center text-gray-500 text-sm py-2">
                            Failed to load user information
                        </div>
                    ) : userDetails ? (
                        <div className="flex gap-3">
                            <AvatarImage
                                name={userDetails.name || 'Unknown User'}
                                filename={userDetails.avatarUri || null}
                                size={48}
                            />
                            <div className="flex-1 min-w-0">
                                <Typography
                                    variant="body1"
                                    className="font-semibold text-gray-900 truncate"
                                    title={userDetails.name || undefined}
                                >
                                    {userDetails.name || 'Unknown User'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className="text-gray-600 text-sm truncate"
                                    title={userDetails.emailAddress || undefined}
                                >
                                    {userDetails.emailAddress || 'No email'}
                                </Typography>
                                {userDetails.department && (
                                    <Typography
                                        variant="caption"
                                        className="text-gray-500 text-xs block mt-1 truncate"
                                        title={userDetails.department}
                                    >
                                        {userDetails.department}
                                    </Typography>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </Popover>
        </>
    );
}
